import { useSyncExternalStore } from "react";
import type {
  UnderwritingAgentStatus,
  UnderwritingReportKey
} from "api/underwriting-agents/types";

/**
 * Try again only queues the agent: until its new check starts, the status still shows the failed one. Reruns asked
 * for in this tab are kept here so the report reads as running in the meantime. A rerun is pending while the report's
 * latest check is still the failed one it was asked for (same `finished_at`, so no client/server clock compare), and
 * for at most RERUN_PENDING_MS, in case the agent never picks it up.
 */
const RERUN_PENDING_MS = 2 * 60_000;

type RerunRequest = { failedFinishedAt: string | null; requestedAt: number };

let requests = new Map<string, RerunRequest>();
const listeners = new Set<() => void>();

const requestKey = (
  applicationId: string,
  applicantId: number,
  reportKey: UnderwritingReportKey
) => `${applicationId}:${applicantId}:${reportKey}`;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => requests;

export const recordRerunRequest = (
  applicationId: string,
  applicantId: number,
  reportKey: UnderwritingReportKey,
  failedFinishedAt: string | null
) => {
  requests = new Map(requests).set(
    requestKey(applicationId, applicantId, reportKey),
    { failedFinishedAt, requestedAt: Date.now() }
  );
  listeners.forEach((listener) => listener());
};

/** Whether Try again was used on this report in this tab; the modal then says "Reviewing … again". */
export const wasRerunRequested = (
  rerunRequests: Map<string, RerunRequest>,
  applicationId: string,
  applicantId: number,
  reportKey: UnderwritingReportKey
) => rerunRequests.has(requestKey(applicationId, applicantId, reportKey));

/** The status with each rerun that the agent hasn't started yet shown as running. */
export const withPendingReruns = (
  status: UnderwritingAgentStatus,
  applicationId: string,
  rerunRequests: Map<string, RerunRequest>
): UnderwritingAgentStatus => {
  const now = Date.now();
  let pending = 0;
  const reports = status.reports.map((report) => {
    const request = rerunRequests.get(
      requestKey(applicationId, report.applicant_id, report.report_key)
    );
    const isPending =
      request &&
      report.status === "failed" &&
      report.finished_at === request.failedFinishedAt &&
      now - request.requestedAt < RERUN_PENDING_MS;
    if (!isPending) return report;

    pending += 1;
    return { ...report, status: "running" as const };
  });
  if (!pending) return status;

  return {
    ...status,
    reports,
    running_count: status.running_count + pending,
    failed_count: Math.max(0, status.failed_count - pending)
  };
};

/** The reruns asked for in this tab; components re-render when one is added. */
export const useRerunRequests = () =>
  useSyncExternalStore(subscribe, getSnapshot);

/** For the status query's polling, which runs outside React. */
export const getRerunRequests = getSnapshot;
